import {
  Box, Typography, Card, Stack, Button, Chip, CircularProgress,
  Alert, Input, IconButton, Tooltip, Table, Sheet, Avatar,
  List,
  ListItem,
  ListItemContent
} from "@mui/joy";
import {
  SearchRounded, DownloadRounded, VisibilityRounded,
  PersonRounded, AssessmentRounded, FilterListRounded,
  CalendarTodayRounded, GradeRounded, MedicalServicesRounded
} from '@mui/icons-material';
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../context";
import { APIGetTestResults, APIDownloadTestResult } from "../../api";
import { useNavigate } from "react-router";
import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";



export default function TestResultsPage() {
  const { currentDoctor, token, setModalResults } = useContext(LayoutContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    patient_id: '',
    test_id: '',
    date_from: '',
    date_to: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [downloading, setDownloading] = useState({});


  // Загрузка результатов тестов
  const loadTestResults = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      // Добавляем текущего пользователя, если он пациент
      if (currentDoctor?.type_profile?.includes('patient')) {
        params.user_id = currentDoctor.id;
      }
      
      const response = await APIGetTestResults(token, params);
      
      if (response.success) {
        setResults(response.results || []);
      } else {
        setError(response.error || 'Ошибка при загрузке результатов');
      }
    } catch (error) {
      console.error("Ошибка при загрузке результатов:", error);
      setError("Ошибка при загрузке результатов тестов");
    } finally {
      setLoading(false);
    }
  };

  // Скачивание результата теста
  const handleDownloadResult = async (resultId) => {
    try {
      setDownloading(prev => ({ ...prev, [resultId]: true }));
      
      const response = await APIDownloadTestResult(token, resultId);
      
      // Создаем blob из полученных данных
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Определяем имя файла из заголовков или используем дефолтное
      const contentDisposition = response.headers['content-disposition'];
      let filename = `test_result_${resultId}.txt`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Ошибка при скачивании:", error);
      setError("Ошибка при скачивании результата");
    } finally {
      setDownloading(prev => ({ ...prev, [resultId]: false }));
    }
  };
  
  // Применение фильтров
  const applyFilters = () => {
    const params = {};
    if (filters.patient_id) params.patient_id = filters.patient_id;
    if (filters.test_id) params.test_id = filters.test_id;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (searchTerm) params.search = searchTerm;
    
    loadTestResults(params);
  };

  // Сброс фильтров
  const resetFilters = () => {
    setFilters({
      patient_id: '',
      test_id: '',
      date_from: '',
      date_to: ''
    });
    setSearchTerm('');
    loadTestResults();
  };

  useEffect(() => {
    loadTestResults();
  }, []);

  // Форматирование даты
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Короткое форматирование даты для мобильных
  const formatDateShort = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Получение типа теста для отображения
  const getTestTypeLabel = (testType) => {
    switch (testType) {
      case 'psychology': return 'Психологический';
      case 'diagnostic': return 'Диагностический';
      case 'screening': return 'Скрининговый';
      default: return 'Скрининговый';
    }
  };

  // Десктопная версия таблицы
  const DesktopTable = () => (
    <Sheet sx={{ height: 'auto', overflow: 'auto' }}>
      <Table
        aria-label="Таблица результатов тестирования"
        stripe="odd"
        hoverRow
        sx={{
          '& thead th': { fontWeight: 'lg' },
          '& tbody tr:hover': { backgroundColor: 'background.level1' },
        }}
      >
        <thead>
          <tr>
            <th>Пациент</th>
            <th>Тест</th>
            <th>Балл</th>
            <th>Дата прохождения</th>
            <th>Врач</th>
            <th style={{ textAlign: 'center' }}>Действия</th>
          </tr>
        </thead>
        <tbody>
          {results.map((result) => (
            <tr key={result.id}>
              <td>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar size="sm">
                    <PersonRounded />
                  </Avatar>
                  <Box>
                    <Typography level="body-sm" fontWeight="lg">
                      {result.user_info?.name || 'Не указано'}
                    </Typography>
                    {result.patient?.phone && (
                      <Typography level="body-xs" color="neutral">
                        {result.patient.phone}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </td>
              <td>
                <Typography level="body-sm">
                  {result.test_info?.name || 'Тест'}
                </Typography>
                {result.test_info?.test_type && (
                  <Typography level="body-xs" color="neutral">
                    {getTestTypeLabel(result.test_info.test_type)}
                  </Typography>
                )}
              </td>
              <td>
                {result.score !== null && result.score !== undefined ? (
                  <Chip
                    size="sm"
                    variant="soft"
                    color={result.score > 70 ? "success" : result.score > 40 ? "warning" : "danger"}
                  >
                    {result.score.toFixed(1)}%
                  </Chip>
                ) : (
                  <Typography level="body-sm" color="neutral">
                    Нет оценки
                  </Typography>
                )}
              </td>
              <td>
                <Typography level="body-sm">
                  {formatDate(result.date_create)}
                </Typography>
              </td>
              <td>
                {result.doctor_info ? (
                  <Typography level="body-sm">
                    {result.doctor_info.name}
                  </Typography>
                ) : (
                  <Typography level="body-sm" color="neutral">
                    Не назначен
                  </Typography>
                )}
              </td>
              <td>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Tooltip title="Просмотреть детали">
                    <IconButton
                      size="sm"
                      variant="outlined"
                      color="primary"
                      onClick={() => setModalResults({ open: true, resultId: result.id })}
                    >
                      <VisibilityRounded />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Скачать результат">
                    <IconButton
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      loading={downloading[result.id]}
                      onClick={() => handleDownloadResult(result.id)}
                    >
                      <DownloadRounded />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );

  // Мобильная версия списка
  const MobileList = () => (
    <List
      sx={{
        '--ListItem-paddingY': '1rem',
        '--ListItem-paddingX': '1rem',
        '--ListItem-borderRadius': '8px',
        '--ListItem-gap': '1rem',
      }}
    >
      {results.map((result) => (
        <Card
          key={result.id}
          variant="outlined"
          sx={{
            mb: 2,
            p: 0,
            '&:hover': {
              boxShadow: 'md',
              borderColor: 'neutral.outlinedHoverBorder',
            },
          }}
        >
          <ListItem>
            <ListItemContent>
              <Stack spacing={2}>
                {/* Пациент */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar size="sm">
                    <PersonRounded />
                  </Avatar>
                  <Box>
                    <Typography level="title-md">
                      {result.user_info?.name || 'Не указано'}
                    </Typography>
                    {result.patient?.phone && (
                      <Typography level="body-xs" color="neutral">
                        {result.patient.phone}
                      </Typography>
                    )}
                  </Box>
                </Stack>

                {/* Тест и тип */}
                <Box>
                  <Typography level="title-sm">
                    {result.test_info?.name || 'Тест'}
                  </Typography>
                  {result.test_info?.test_type && (
                    <Typography level="body-xs" color="neutral">
                      {getTestTypeLabel(result.test_info.test_type)}
                    </Typography>
                  )}
                </Box>

                {/* Оценка и дата */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  {result.score !== null && result.score !== undefined ? (
                    <Chip
                      size="sm"
                      variant="soft"
                      color={result.score > 70 ? "success" : result.score > 40 ? "warning" : "danger"}
                      startDecorator={<GradeRounded />}
                    >
                      {result.score.toFixed(1)}%
                    </Chip>
                  ) : (
                    <Chip size="sm" variant="outlined" color="neutral">
                      Нет оценки
                    </Chip>
                  )}
                  
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CalendarTodayRounded sx={{ fontSize: '0.875rem', color: 'neutral.500' }} />
                    <Typography level="body-xs" color="neutral">
                      {formatDateShort(result.date_create)}
                    </Typography>
                  </Stack>
                </Stack>

                {/* Врач */}
                {result.doctor_info && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MedicalServicesRounded sx={{ fontSize: '0.875rem', color: 'neutral.500' }} />
                    <Typography level="body-sm">
                      Врач: {result.doctor_info.name}
                    </Typography>
                  </Stack>
                )}

                {/* Действия */}
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    startDecorator={<VisibilityRounded />}
                    onClick={() => setModalResults({ open: true, resultId: result.id })}
                    fullWidth
                  >
                    Подробнее
                  </Button>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    startDecorator={<DownloadRounded />}
                    loading={downloading[result.id]}
                    onClick={() => handleDownloadResult(result.id)}
                    fullWidth
                  >
                    Скачать
                  </Button>
                </Stack>
              </Stack>
            </ListItemContent>
          </ListItem>
        </Card>
      ))}
    </List>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: 2,
          pb: { xs: 2, sm: 2, md: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          gap: 2,
        }}
      >
        {/* Заголовок и кнопки */}
        <Box
          sx={{
            display: 'flex',
            mb: 1,
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'start', sm: 'center' },
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Typography level="h2" component="h1" startDecorator={<AssessmentRounded />}>
            Результаты тестирования
          </Typography>
          
          <Stack direction="row" spacing={1}>
            <Button
              variant={showFilters ? "solid" : "outlined"}
              color="neutral"
              startDecorator={<FilterListRounded />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Фильтры
            </Button>
          </Stack>
        </Box>

        {/* Сообщения об ошибках/успехах */}
        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Поиск */}
        <Input
          size="sm"
          placeholder="Поиск по пациентам или тестам..."
          startDecorator={<SearchRounded />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
          endDecorator={
            <Button size="sm" onClick={applyFilters}>
              Найти
            </Button>
          }
        />

        {/* Фильтры */}
        {showFilters && (
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography level="title-sm" sx={{ mb: 2 }}>
              Фильтры
            </Typography>
            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
              <Input
                size="sm"
                placeholder="ID пациента"
                value={filters.patient_id}
                onChange={(e) => setFilters(prev => ({ ...prev, patient_id: e.target.value }))}
                sx={{ flex: 1 }}
              />
              <Input
                size="sm"
                placeholder="ID теста"
                value={filters.test_id}
                onChange={(e) => setFilters(prev => ({ ...prev, test_id: e.target.value }))}
                sx={{ flex: 1 }}
              />
              <Input
                size="sm"
                type="date"
                placeholder="С даты"
                value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
              />
              <Input
                size="sm"
                type="date"
                placeholder="По дату"
                value={filters.date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
              />
              <Button variant="outlined" color="neutral" onClick={resetFilters}>
                Сбросить
              </Button>
              <Button onClick={applyFilters}>
                Применить
              </Button>
            </Stack>
          </Card>
        )}

        {/* Таблица результатов */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : results.length > 0 ? (
          <>
            {/* Показываем количество результатов на мобильных */}
            {isMobile && (
              <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Найдено результатов: {results.length}
              </Typography>
            )}
            
            {isMobile ? <MobileList /> : <DesktopTable />}
          </>
        ) : (
          <Alert color="neutral" sx={{ mt: 2 }}>
            Результаты тестирования не найдены
          </Alert>
        )}
      </Box>
    </Box>
  );
}